"use client";

import { cn } from "@reecost/ui";
import Image from "next/image";
import type { AuthIntent, BusinessType, BusinessTypeOption } from "./types";

export const BUSINESS_TYPE_OPTIONS: BusinessTypeOption[] = [
  {
    value: "retail",
    title: "reecost Retail",
    badge: "Instant Access",
    description: {
      login:
        "For retail stores and supermarkets. Sign in instantly with your team credentials.",
      signup:
        "For retail stores and supermarkets. Create your account and start onboarding instantly.",
    },
    icon: "/static/images/login/retail.png",
  },
  {
    value: "restaurant",
    title: "reecost Restaurant",
    badge: "Contact Us",
    description: {
      login:
        "For restaurants, cafés, QSRs and bars. Use your POS credentials to sign in.",
      signup:
        "For restaurants, cafés, QSRs and bars. Book a free demo and our team will set up your access.",
    },
    icon: "/static/images/login/restaurant-pos.png",
  },
];

const BADGE_CONFIG: Record<
  BusinessType,
  Record<AuthIntent, { text: string; className: string }>
> = {
  retail: {
    login: {
      text: "Sign in instantly with your team credentials",
      className: "border-[#00A091] bg-[#f3f3f3] text-[#00A091]",
    },
    signup: {
      text: "Instant Access",
      className: "border-emerald-700 bg-emerald-50 text-emerald-700",
    },
  },
  restaurant: {
    login: {
      text: "Use POS credentials provided by our team",
      className: "border-[#0064E1] bg-[#E9EFFC] text-[#0064E1]",
    },
    signup: {
      text: "Contact Us",
      className: "border-amber-700 bg-amber-50 text-amber-700",
    },
  },
};

export const BusinessTypeSelector = ({
  value,
  onSelect,
  intent,
}: {
  value: BusinessType | null;
  onSelect: (next: BusinessType) => void;
  intent: AuthIntent;
}) => {
  return (
    <div className="space-y-3">
      {BUSINESS_TYPE_OPTIONS.map((option) => {
        const selected = value === option.value;
        const badge = BADGE_CONFIG[option.value][intent];

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={cn(
              "w-full rounded-[14px] border bg-[#faf8ff] p-3 text-left transition-all duration-200 sm:p-4",
              "hover:border-[#7c3aed] hover:bg-white hover:shadow-[0_8px_20px_rgba(124,58,237,0.12)]",
              selected
                ? "border-[#7c3aed] bg-[#7c3aed]/5 shadow-sm"
                : "border-[#e7defb]",
            )}
          >
            <div className="flex items-start gap-3">
              <div className="relative size-10 shrink-0 overflow-hidden rounded-xl bg-gray-100 sm:size-12">
                <Image
                  alt={option.title}
                  src={option.icon}
                  fill
                  sizes="50px"
                  unoptimized
                  className="object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex flex-col items-start gap-1.5">
                  <h3 className="font-semibold text-[#0f0f1a] text-base sm:text-lg">
                    {option.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        "rounded-full border px-2 py-0.5 font-medium text-[11px] leading-tight sm:text-xs",
                        badge.className,
                      )}
                    >
                      {badge.text}
                    </span>
                    <span
                      aria-hidden
                      className="inline-flex size-4 items-center justify-center rounded-full bg-slate-500 font-semibold text-[10px] text-white"
                    >
                      i
                    </span>
                  </div>
                </div>
                <p className="mt-1.5 text-[#5b5b6b] text-xs leading-relaxed sm:text-sm">
                  {option.description[intent]}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
